/**
 * A central event bus for communication between components
 */
export class EventBus {
  private static instance: EventBus;
  private listeners: Map<string, Function[]> = new Map();

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get the singleton instance of EventBus
   */
  public static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  /**
   * Subscribe to an event
   * @param event The event name to subscribe to
   * @param callback The callback function to execute when the event is published
   */
  public subscribe(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  /**
   * Unsubscribe from an event
   * @param event The event name to unsubscribe from
   * @param callback The callback function to remove
   */
  public unsubscribe(event: string, callback: Function): void {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event)!;
    const index = callbacks.indexOf(callback);
    
    if (index !== -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * Publish an event
   * @param event The event name to publish
   * @param data The data to pass to the callback functions
   */
  public publish(event: string, data?: any): void {
    if (!this.listeners.has(event)) return;
    
    this.listeners.get(event)!.forEach(callback => {
      callback(data);
    });
  }

  /**
   * Clear all listeners for an event
   * @param event The event name to clear listeners for
   */
  public clearListeners(event: string): void {
    this.listeners.delete(event);
  }

  /**
   * Clear all listeners for all events
   */
  public clearAllListeners(): void {
    this.listeners.clear();
  }
}

// Export a singleton instance
export const eventBus = EventBus.getInstance();